import { Link, useNavigate } from "react-router-dom";
import { Layers, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmtGNF } from "@/lib/format";
import { mediaUrl } from "@/lib/api";

export default function Cart() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="cart-empty">
        <ShoppingCart className="mx-auto h-12 w-12 text-slate-300" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Votre panier est vide</h1>
        <p className="mt-2 text-sm text-slate-500">Parcourez le catalogue et ajoutez vos produits.</p>
        <Link to="/catalogue" className="ds-btn-primary mt-5" data-testid="cart-go-catalog">
          Voir le catalogue
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="cart-page">
      <h1 className="ds-h1">Mon panier</h1>
      <p className="mt-1 text-sm text-slate-500">{items.length} article(s)</p>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.product_id} className="ds-card flex gap-3 p-3" data-testid={`cart-line-${i.product_id}`}>
              <Link to={`/produit/${i.product_id}`} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                {i.image && <img src={mediaUrl(i.image)} alt="" className="h-full w-full object-cover" />}
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={`/produit/${i.product_id}`} className="line-clamp-2 text-sm font-semibold text-slate-900 hover:text-brand">
                  {i.name}
                </Link>
                <p className="font-num mt-0.5 text-xs text-slate-500">{i.sku}</p>
                <p className="font-num mt-1 text-sm font-bold text-brand">{fmtGNF(i.unit_price)}</p>
                {i.is_wholesale && (
                  <span className="ds-chip mt-1 border-gold bg-gold-light text-gold-dark">
                    <Layers className="h-3 w-3" /> Prix de gros
                  </span>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center rounded-lg border border-slate-300">
                    <button
                      type="button"
                      onClick={() => setQty(i.product_id, i.quantity - 1)}
                      className="grid h-8 w-8 place-items-center text-slate-600"
                      data-testid={`cart-decrement-${i.product_id}`}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="font-num w-10 text-center text-sm" data-testid={`cart-qty-${i.product_id}`}>
                      {i.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty(i.product_id, Math.min(i.quantity + 1, i.stock || 999))}
                      className="grid h-8 w-8 place-items-center text-slate-600"
                      data-testid={`cart-increment-${i.product_id}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(i.product_id)}
                    className="flex items-center gap-1 text-xs font-semibold text-rose-600 hover:underline"
                    data-testid={`cart-remove-${i.product_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Retirer
                  </button>
                </div>
              </div>
              <p className="font-num shrink-0 text-sm font-bold text-slate-900">{fmtGNF(i.total)}</p>
            </div>
          ))}
          <button type="button" onClick={clear} className="text-xs font-semibold text-slate-500 hover:text-rose-600" data-testid="cart-clear">
            Vider le panier
          </button>
        </div>

        <div className="ds-card h-fit p-4 lg:sticky lg:top-24">
          <h2 className="font-display text-lg font-bold text-slate-900">Récapitulatif</h2>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Sous-total</span>
              <span className="font-num font-semibold" data-testid="cart-subtotal">{fmtGNF(subtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Frais de livraison</span>
              <span className="text-xs">calculés à l'étape suivante</span>
            </div>
            {subtotal >= 100000 ? (
              <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="cart-free-delivery">
                Livraison offerte : votre panier dépasse 100 000 GNF.
              </p>
            ) : (
              <p className="rounded-xl bg-brand-light px-3 py-2 text-xs text-brand" data-testid="cart-free-delivery-hint">
                Livraison offerte dès 100 000 GNF — il vous manque {fmtGNF(100000 - subtotal)}.
              </p>
            )}
          </div>
          <button type="button" onClick={() => navigate("/commander")} className="ds-btn-primary mt-4 w-full" data-testid="cart-checkout-button">
            Passer la commande
          </button>
          <Link to="/catalogue" className="ds-btn-ghost mt-2 w-full">Continuer mes achats</Link>
          <p className="mt-3 text-center text-[11px] text-slate-500" data-testid="cart-return-policy">
            Retour possible sous 48 h après la livraison si l'article est en bon état, complet et dans son emballage.
          </p>
        </div>
      </div>
    </div>
  );
}
